import {
  Language,
  ProjectTemplateCompatibility,
  Promotion,
} from '@sparemin/api-sdk';
import * as Immutable from 'immutable';
import { createSelector } from 'reselect';
import { CreationResponse } from 'redux/middleware/api/creation-service/types';
import { NormalizedVideoUpload } from 'redux/middleware/api/media-upload-service';
import {
  PodcastDetailEpisode,
  PodcastEpisode,
  PodcastFeedEpisode,
  PodcastRemoteEpisode,
} from 'redux/middleware/api/podcast-service';
import { ProjectListItem } from 'redux/middleware/api/video-project-management-service/types';
import { State } from 'redux/types';
import { DeepImmutableMap } from 'types';
import { podcastWorkflowTemplateSelector } from './podcastWorkflowTemplates';
import { videoSelector } from './videos';

export const callEntriesSelector = state =>
  state.getIn(['entities', 'callEntries']);
export const imagesSelector = state => state.getIn(['entities', 'images']);
export const transcriptsSelector = state =>
  state.getIn(['entities', 'transcripts']);
export const projectsSelector = state =>
  state.getIn(['entities', 'projects'], Immutable.Map());
export const waveformsSelector = state =>
  state.getIn(['entities', 'waveforms']);
export const keywordsSelector = state => state.getIn(['entities', 'keywords']);
export const manualTranscriptsSelector = state =>
  state.getIn(['entities', 'manualTranscripts']);
export const personalFontsSelector = state =>
  state.getIn(['entities', 'personalFonts']);
export const googleFontsSelector = state =>
  state.getIn(['entities', 'googleFonts']);
export const userPreferencesSelector = state =>
  state.getIn(['entities', 'userPreferences']);
export const videoSizesSelector = state =>
  state.getIn(['entities', 'videoSizes']);
export const eddySupportedProjectLanguagesSelector = state =>
  state.getIn(['entities', 'eddySupportedProjectLanguages']);
export const embedExportsSelector = state =>
  state.getIn(['entities', 'embedExports']);
export const sampleAudiosSelector = state =>
  state.getIn(['entities', 'sampleAudios']);
export const announcementsSelector = state =>
  state.getIn(['entities', 'announcements']);
export const textBlobsSelector = state =>
  state.getIn(['entities', 'textBlobs']);
export const summariesSelector = state =>
  state.getIn(['entities', 'summaries']);
export const tracesSelector = state => state.getIn(['entities', 'traces']);
export const revisionHistorySelector = state =>
  state.getIn(['entities', 'revisionHistory'], Immutable.Map());
export const projectTemplatesSelector = state =>
  state.getIn(['entities', 'projectTemplates'], Immutable.Map());
export const plansSelector = state => state.getIn(['entities', 'plans']);
export const promotionsSelector = state =>
  state.getIn(['entities', 'promotions']);
export const podcastsSelector = state => state.getIn(['entities', 'podcasts']);
export const podcastDetailEpisodesSelector = state =>
  state.getIn(['entities', 'podcastDetailEpisodes']);
export const podcastSubscriptionsSelector = state =>
  state.getIn(['entities', 'podcastSubscriptions']);
export const entireAudioInstancesSelector = state =>
  state.getIn(['entities', 'entireAudioInstances']);
export const entireAudiosSelector = state =>
  state.getIn(['entities', 'entireAudios']);
export const entireAudioClipsSelector = state =>
  state.getIn(['entities', 'entireAudioClips']);
export const episodeClipSuggestionSelector = state =>
  state.getIn(['entities', 'episodeClipSuggestion']);
export const podcastFeedsSelector = state =>
  state.getIn(['entities', 'podcastFeeds']);
export const podcastFeedEpisodesSelector = state =>
  state.getIn(['entities', 'podcastFeedEpisodes']);
export const userProfilesSelector = state =>
  state.getIn(['entities', 'userProfiles']);
export const favoritePodcastsSelector = state =>
  state.getIn(['entities', 'favoritePodcasts']);
export const autoCreateEpisodeVideosSelector = state =>
  state.getIn(['entities', 'autoCreateEpisodeVideos']);
export const autoCreateEpisodesSelector = state =>
  state.getIn(['entities', 'autoCreateEpisodes']);
export const audioVariationsSelector = state =>
  state.getIn(['entities', 'audioVariations']);
export const displayPrefsSelector = state =>
  state.getIn(['entities', 'displayPrefs']);
export const userPrefsSelector = state =>
  state.getIn(['entities', 'userPrefs']);
export const waveformPrefsSelector = state =>
  state.getIn(['entities', 'waveformPrefs']);
export const creationsSelector = state =>
  state.getIn(['entities', 'creations']);
export const experimentsSelector = state =>
  state.getIn(['entities', 'experiments']);
export const podcastRemoteEpisodesSelector = state =>
  state.getIn(['entities', 'podcastRemoteEpisodes']);
export const podcastEpisodesSelector = state =>
  state.getIn(['entities', 'podcastEpisodes']);
export const socialSharesSelector = state =>
  state.getIn(['entities', 'socialShares']);
export const youtubeLanguagesSelector = state =>
  state.getIn(['entities', 'youtubeLanguages']);
export const textPresetsSelector = state =>
  state.getIn(['entities', 'textPresets']);
export const subscriptionItemInformationSelector = state =>
  state.getIn(['entities', 'subscriptionItemInformation']);
export const notificationAlertSelector = state =>
  state.getIn(['entities', 'notificationAlert', 'alerts']);
export const clipSuggestionsCreationProgressSelector = state =>
  state.getIn(['entities', 'creationSuggestionsProgress', 'progress']);
export const videoSocialPostCaptionsSelector = state =>
  state.getIn(['entities', 'videoSocialPostCaptions']);
export const embedVideoSocialPostCaptionsSelector = state =>
  state.getIn(['entities', 'embedVideoSocialPostCaptions']);
export const videoZipRequestsByIdSelector = state =>
  state.getIn(['entities', 'videoZipRequestsById']);
export const automationSupportedLanguagesSelector = state =>
  state.getIn(['entities', 'automationSupportedLanguages']);
export const captionSupportedLanguagesSelector = state =>
  state.getIn(['entities', 'captionSupportedLanguages']);
export const entireVideoInstancesSelector = state =>
  state.getIn(['entities', 'entireVideoInstances']);
export const entireVideosSelector = state =>
  state.getIn(['entities', 'entireVideos']);

// Picks the id of the first entire-video that can be reused for an upload: an
// md5 match that hasn't terminally failed. The match may still be pending, so
// the caller polls it by id until it settles. `waitFor === 'waveform'` also
// skips matches whose waveform generation has already failed.
export const reusableEntireVideoSelector = (
  state: State,
  ids: number[],
  waitFor: 'entireVideo' | 'waveform',
): number | undefined => {
  const entireVideos = entireVideosSelector(state);

  const match = ids
    .map(id => entireVideos?.get(String(id)))
    .find(entry => {
      if (!entry || entry.get('status') === 'failed') {
        return false;
      }

      if (waitFor !== 'waveform') {
        return true;
      }

      const waveformStatus = entry.getIn(['waveform', 'status']);

      return waveformStatus !== 'failed' && waveformStatus !== 'failedAcked';
    });

  return match?.get('id') as number | undefined;
};

export const entireVideoSelector = (
  state: State,
  entireVideoId: number | string,
) => entireVideosSelector(state)?.get(String(entireVideoId));

export const widgetTemplateCompatibilitySelector = (
  state,
): DeepImmutableMap<Record<string, ProjectTemplateCompatibility>> =>
  state.getIn(['entities', 'widgetTemplateCompatibility']);

export const templateAutoFramePreferenceSelector = (id: string) => {
  return createSelector(
    podcastWorkflowTemplateSelector(id),
    (template): boolean | null | undefined =>
      template?.defaultCreationOptions?.videoEffects?.autoFrame,
  );
};

export const makePromotionByKeySelector = (promotionKey: string) => {
  return createSelector(promotionsSelector, promotions => {
    return promotions
      ?.find(promotion => promotion.get('promotionKey') === promotionKey)
      ?.toJS() as Promotion;
  });
};

export const userProfileSelector = (userId: number) => {
  return createSelector(userProfilesSelector, userProfiles => {
    return userProfiles?.get(String(userId))?.toJS();
  });
};

export const podcastDetailEpisodeSelector = (episodeId: string) => {
  return createSelector(podcastDetailEpisodesSelector, podcastEpisodes => {
    return podcastEpisodes?.get(episodeId)?.toJS() as PodcastDetailEpisode;
  });
};

export const podcastRemoteEpisodeSelector = (episodeId: string) => {
  return createSelector(podcastRemoteEpisodesSelector, episodes => {
    return episodes?.get(episodeId)?.toJS() as PodcastRemoteEpisode;
  });
};

export const makePodcastEpisodeSelector = (episodeId: number) => {
  return createSelector(podcastEpisodesSelector, podcastEpisodes => {
    return podcastEpisodes?.get(String(episodeId))?.toJS() as PodcastEpisode;
  });
};

export const makePodcastFeedEpisodeSelector = (episodeId: string) =>
  createSelector(
    podcastFeedEpisodesSelector,
    episodes => episodes?.get(episodeId)?.toJS() as PodcastFeedEpisode,
  );

export const eddySupportedLanguagesSelector = createSelector(
  eddySupportedProjectLanguagesSelector,
  (eddySupportedLanguages): Language[] =>
    eddySupportedLanguages?.valueSeq()?.toJS() || [],
);

export const automationSupportedLanguagesListSelector = createSelector(
  automationSupportedLanguagesSelector,
  (automationSupportedLanguages): Language[] =>
    automationSupportedLanguages
      ?.valueSeq()
      ?.toJS()
      .sort((a, b) => a.originalPosition - b.originalPosition) || [],
);

export const captionSupportedLanguagesListSelector = createSelector(
  captionSupportedLanguagesSelector,
  (captionSupportedLanguages): Language[] =>
    captionSupportedLanguages
      ?.valueSeq()
      .toJS()
      .sort((a, b) => a.originalPosition - b.originalPosition) ?? [],
);

export const makeCreationSelector = (creationId: number) =>
  createSelector(
    creationsSelector,
    creations => creations?.get(String(creationId))?.toJS() as CreationResponse,
  );

export const makeVideoSelector = (videoId: number | string) =>
  createSelector(
    [state => videoSelector(state, videoId)],
    video => video?.toJS() as NormalizedVideoUpload | undefined,
  );

export const hasNotificationAlertSelector = createSelector(
  notificationAlertSelector,
  notification =>
    notification ? notification.toJS().hasNewSocialShareData : false,
);

export const projectsDetailsSelector = createSelector(
  projectsSelector,
  projects => projects.map(project => project.get('detail')),
);

export const makeProjectSelector = (projectId: string | number) => (
  state: State,
): ProjectListItem | undefined =>
  projectsSelector(state)
    ?.get(projectId?.toString())
    ?.toJS();

export const makeProjectDetailSelector = (projectId: string) => (
  state: State,
) => {
  const { detail, projectType } = makeProjectSelector(projectId)(state) ?? {};

  return projectType === 'plainProject' ? detail : undefined;
};

export const makeProjectNameSelector = (projectId: string) => (
  state: State,
) => {
  const { detail, projectType } = makeProjectSelector(projectId)(state) ?? {};

  return projectType === 'plainProject'
    ? detail?.projectConfig?.projectName
    : detail?.name;
};

export const makeProjectReprAtSelector = (projectId: string) => (
  state: State,
) => {
  const { reprAt } = makeProjectSelector(projectId)(state) ?? {};

  return reprAt;
};

export const makeProjectWidgetUuidSelector = (projectId: string) => (
  state: State,
) => {
  const detail = makeProjectDetailSelector(projectId)(state);

  return detail?.latestSuccessfulVideo?.widgetUuid;
};

export const makeProjectEmbedConfigurationUuidSelector = (
  projectId: string,
) => (state: State) => {
  const detail = makeProjectDetailSelector(projectId)(state);

  return detail?.projectConfig?.embedConfigurationUuid;
};

export const makeProjectClipCountSelector = (projectId: string) => (
  state: State,
) => {
  const { detail, projectType } = makeProjectSelector(projectId)(state) ?? {};

  if (projectType !== 'plainProject') {
    return detail?.clipCount;
  }

  return undefined;
};

export const makeProjectAspectRatioSelector = (projectId: string) => (
  state: State,
) => {
  const { detail, projectType } = makeProjectSelector(projectId)(state) ?? {};

  const aspectRatio =
    projectType === 'plainProject'
      ? detail?.projectConfig?.aspectRatio
      : detail?.aspectRatio;

  return aspectRatio;
};

export const makeProjectCreateMethodSelector = (projectId: string) => (
  state: State,
) => {
  const detail = makeProjectDetailSelector(projectId)(state);

  return detail?.createMethod;
};

export const makeProjectConfigSelector = (projectId: string) => (
  state: State,
) => {
  const detail = makeProjectDetailSelector(projectId)(state);

  return detail?.projectConfig;
};
